import React from 'react'
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, Divider, Chip, Stack } from '@mui/material'

// RTO band -> chip colour (green low risk -> red high risk).
const BAND_COLOR = { RED: 'error', ORANGE: 'warning', YELLOW: 'warning', GREEN: 'success', GREY: 'default' }

/**
 * One model parameter's weightage row: label + share-of-total bar + signed impact.
 * `c` is one entry of the /rto/score `all_contributions` array
 * ({ code, label, value, shap_delta, weight_pct, direction }).
 * direction: raises -> red, lowers -> green, neutral -> grey.
 * `dense` tightens it for inline grid-cell use; full size for dialogs.
 */
export const ContributionRow = ({ c, dense }) => {
  const share = Number(c.weight_pct || 0)
  const barColor = c.direction === 'raises' ? '#e53935' : c.direction === 'lowers' ? '#43a047' : '#9e9e9e'
  return (
    <Box sx={{ mb: dense ? 0.5 : 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
        <Typography variant='caption' noWrap sx={{ maxWidth: dense ? 150 : 320 }}>{c.label}</Typography>
        <Typography variant='caption' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {share.toFixed(0)}%
          <Box component='span' sx={{ color: 'text.secondary', ml: 0.5 }}>
            ({c.shap_delta > 0 ? '+' : ''}{Number(c.shap_delta).toFixed(2)})
          </Box>
        </Typography>
      </Box>
      <Box sx={{ height: dense ? 4 : 6, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.08)', mt: 0.25 }}>
        <Box sx={{ height: dense ? 4 : 6, borderRadius: 2, width: `${Math.min(share, 100)}%`, bgcolor: barColor }} />
      </Box>
    </Box>
  )
}

/**
 * Full per-parameter weightage dialog — every model feature (risk-raising and
 * protective) with its share of the score and raw SHAP delta. Shared by the RTO
 * dashboard "Why" column and the order / order-detail RtoRiskCard so both read
 * identically.
 */
export const ContributionsDialog = ({ open, onClose, contributions = [], band, score, subtitle, hygieneBlock }) => (
  <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth onClick={(e) => e.stopPropagation()}>
    <DialogTitle sx={{ pr: 6 }}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='h6' component='span'>Parameter weightage</Typography>
        {band && <Chip size='small' color={BAND_COLOR[band] || 'default'} label={band} />}
        {score != null && <Chip size='small' variant='outlined' label={`Score ${Number(score).toFixed(0)}`} />}
      </Stack>
      {subtitle && (
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>{subtitle}</Typography>
      )}
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label='close'>✕</IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {hygieneBlock && (
        <Typography variant='body2' color='error.main' fontWeight={700} sx={{ mb: 1.5 }}>
          Hygiene block — the model did not run; the score was forced by a deterministic rule.
        </Typography>
      )}
      {contributions.length ? contributions.map((c) => <ContributionRow key={c.code} c={c} />) : (
        <Typography variant='body2' color='text.secondary'>No per-parameter breakdown available for this order.</Typography>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant='caption' color='text.secondary' display='block'>
        <b>%</b> = share of the model&apos;s total decision movement (|Δ| ÷ Σ|Δ|).
        <br /><b>(Δ)</b> = raw SHAP contribution in log-odds.
        <br /><Box component='span' sx={{ color: '#e53935' }}>Red</Box> raises RTO risk · <Box component='span' sx={{ color: '#43a047' }}>green</Box> lowers it.
      </Typography>
    </DialogContent>
  </Dialog>
)
